import { AuthenticationResult, EventType } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { Provider } from "react-redux";
import ReactDOM from "react-dom/client";
import "./index.scss";
import App from "./App.tsx";
import { BrowserRouter } from "react-router";
import { loginRequest, msalInstance } from "./msalConfig";
import { store } from "./store";
import { getAccessTokenFromLocal } from "./utils/common";

const getLoginHintFromStoredToken = (): string | undefined => {
  const token = getAccessTokenFromLocal();
  if (!token) return undefined;
  try {
    const claims = JSON.parse(atob(token.split(".")[1]));
    return claims.preferred_username || claims.upn || claims.email;
  } catch {
    return undefined;
  }
};

msalInstance.initialize().then(async () => {
  // MSAL v4 encrypts its localStorage cache with a key held in a session cookie,
  // so after the last browser window closes the cache is unreadable and
  // getAllAccounts() is empty even though the app's own token is still stored.
  // Rebuild the MSAL account from the Entra ID session before the app renders.
  if (msalInstance.getAllAccounts().length === 0 && getAccessTokenFromLocal()) {
    try {
      const response = await msalInstance.ssoSilent({
        ...loginRequest,
        loginHint: getLoginHintFromStoredToken(),
      });
      msalInstance.setActiveAccount(response.account);
    } catch (err) {
      console.log("Silent account recovery failed", err);
    }
  }

  // Default to using the first account if no account is active on page load
  if (
    !msalInstance.getActiveAccount() &&
    msalInstance.getAllAccounts().length > 0
  ) {
    // Account selection logic is app dependent. Adjust as needed for different use cases.
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
  }

  // Optional - This will update account state if a user signs in from another tab or window
  msalInstance.enableAccountStorageEvents();

  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event?.payload) {
      const payload = event.payload as AuthenticationResult; // Cast the payload to AuthenticationResult
      const account = payload.account;
      msalInstance.setActiveAccount(account);
    }
  });

  const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement,
  );

  root.render(
    <MsalProvider instance={msalInstance}>
      <BrowserRouter>
        <Provider store={store}>
          <App />
        </Provider>
      </BrowserRouter>
    </MsalProvider>,
  );
});
